import {
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemButtonProps,
  ListProps,
  Theme,
  Typography,
  TypographyProps,
} from '@mui/joy';
import { listItemClasses } from '@mui/joy/ListItem';
import ListItemContent, {
  listItemContentClasses,
} from '@mui/joy/ListItemContent';
import { mergeProps, useId } from '@react-aria/utils';
import clsx from 'clsx';
import React, { ReactNode, useEffect, useState } from 'react';
import {
  NavLink,
  NavLinkProps,
  resolvePath,
  useLocation,
} from 'react-router-dom';

import { Popover, PopoverContent, PopoverTrigger } from './popover';

export function NavList({
  collapsed = false,
  ...props
}: Readonly<ListProps & { collapsed?: boolean }>) {
  return (
    <List
      {...mergeProps(props, {
        size: 'lg',
        sx: {
          '--ListItem-paddingY': 0,
          '--List-padding': 0,
          '--List-nestedInsetStart': (theme) => theme.spacing(4),
          '--ListItem-radius': (theme) => theme.vars.radius.sm,
          ...(collapsed
            ? {
              [`& .${listItemClasses.root}`]: {
                p: 0,
                justifyContent: 'center',
              },
            }
            : {}),
        },
      } as ListProps)}
    // data-collapsed={JSON.stringify(collapsed)}
    />
  );
}

export function ExpandableListItem({
  id: listId,
  defaultExpanded = false,
  collapsed = false,
  expanded = false,
  onExpanded,
  trigger,
  items = [],
}: Readonly<{
  id?: string;
  expanded?: boolean;
  collapsed?: boolean;
  defaultExpanded?: boolean;
  onExpanded?: (expanded: boolean) => void;
  trigger?: Pick<
    NavItemProps,
    'label' | 'href' | 'startDecorator' | 'endDecorator' | 'collapsed' | 'end'
  >;
  items?: Array<
    Pick<
      NavItemProps,
      'label' | 'href' | 'startDecorator' | 'endDecorator' | 'collapsed' | 'end'
    > & { component?: ReactNode }
  >;
}>) {
  const [open, setOpen] = useState(defaultExpanded);
  useEffect(() => {
    setOpen(expanded);
  }, [expanded]);
  useId(listId);

  const [popoverOpen, setPopoverOpen] = useState(false);
  const pathname = useLocation().pathname;
  useEffect(() => {
    if (pathname && collapsed) {
      setPopoverOpen(false);
    }
  }, [pathname, collapsed]);

  if (collapsed) {
    return (
      <ListItem>
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <ListItemButton
              slots={{ root: IconButton }}
              slotProps={{
                root: {
                  size: 'lg',
                  className: 'dark',
                },
              }}
              sx={navItemOverride}
            >
              {trigger ? trigger.startDecorator : null}
            </ListItemButton>
          </PopoverTrigger>
          <PopoverContent bleed side='right' align='start' sideOffset={20}>
            <NavList>
              {items
                .filter((item) => 'label' in item)
                .map(({ component, ...item }, index) => (
                  <ListItem key={index}>
                    {component ?? <NavItem {...item} />}
                  </ListItem>
                ))}
            </NavList>
          </PopoverContent>
        </Popover>
      </ListItem>
    );
  }
  return (
    <ListItem data-open={JSON.stringify(open)} className='group' nested>
      {trigger ? (
        <NavItem
          data-expandable={true}
          {...trigger}
          href={trigger.href}
          onClick={() => {
            /**
             * if we are on a page of one of the items' pages
             * we should prevent the menu from closing
             * if we are trying to navigate to the trigger page
             */
            const isOnItemPage = items.some(
              (item) =>
                item.href !== undefined &&
                pathname.startsWith(resolvePath(item.href).pathname),
            );
            if (isOnItemPage && open) {
              return;
            }

            setOpen((prev) => !prev);
            if (onExpanded) {
              onExpanded(!open);
            }
          }}
        />
      ) : null}

      <div
        className={clsx([
          'grid transition-all ease-out duration-400',
          '*:overflow-hidden',
          open && 'grid-rows-[1fr] h-auto',
          !open && 'grid-rows-[0fr] h-0 ',
        ])}
      >
        <NavList>
          {items
            .filter((item) => 'label' in item && item.label)
            .map(({ component, ...item }, index) => (
              <ListItem key={index}>
                {component ?? <NavItem {...item} />}
              </ListItem>
            ))}
        </NavList>
      </div>
    </ListItem>
  );
}

export const NestedIndicator = () => {
  return (
    <svg
      className='fill-current'
      width='10'
      height='10'
      viewBox='0 0 10 10'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M9.30447 4.30566L5.69296 0.693973C5.30971 0.310702 4.69124 0.307845 4.31158 0.68759L0.687486 4.31245C0.30782 4.69219 0.310727 5.31074 0.693979 5.69401L4.30549 9.30569C4.68874 9.68897 5.30721 9.69182 5.68688 9.31208L9.31097 5.68722C9.69063 5.30748 9.68773 4.68893 9.30447 4.30566Z'
        fill='currentColor'
      />
    </svg>
  );
};

export const navItemOverride = (theme: Theme) => ({
  marginBlockStart: 0,
  fontSize: theme.fontSize.sm,
  fontWeight: theme.fontWeight.md,
  '--variant-plainHoverBg': `rgba(${theme.palette.neutral.mainChannel} / 0.2)`,
  '--variant-plainColor': theme.palette.neutral[300],
  '--variant-plainActiveBg': `rgba(${theme.palette.neutral.mainChannel} / 0.3)`,
  '&[aria-current=page]:not([data-expandable])': {
    '--variant-plainColor': theme.palette.primary[500],
    '--variant-plainHoverColor': theme.palette.primary[500],
    '--Icon-color': theme.palette.primary[500],
    backgroundColor: `rgba(${theme.palette.primary.mainChannel} / 0.2)`,
  },
  '&:hover': {
    '--Icon-color': theme.palette.primary[500],
    '--variant-plainHoverBg': `rgba(${theme.palette.primary.mainChannel} / 0.2)`,
    '--variant-plainColor': theme.palette.primary[500],
    '--variant-plainHoverColor': theme.palette.primary[500],
    '--variant-plainActiveBg': `rgba(${theme.palette.primary.mainChannel} / 0.3)`,
  },
  '--Icon-fontSize': '1.25rem',
  ['&[data-collapsed], .group[data-sidebar-collapsed] &']: {
    px: 0,
    py: theme.spacing(1.25),
    justifyContent: 'center',
    '--Icon-fontSize': '1.5rem',
  },
  [`.group[data-sidebar-collapsed] & .${listItemContentClasses.root}`]: {
    display: 'none',
  },
});

type NavItemProps = Omit<ListItemButtonProps, 'children'> & {
  href?: NavLinkProps['to'];
  label?: React.ReactNode;
  collapsed?: boolean;
  children?: React.ReactNode;
  startDecorator?: React.ReactNode;
  endDecorator?: React.ReactNode;
} & Pick<NavLinkProps, 'end'>;

export function NavItem({
  component,
  startDecorator,
  endDecorator,
  label = null,
  collapsed = false,
  children,
  href,
  ...props
}: Readonly<NavItemProps>) {
  if (collapsed) {
    return (
      <CollapsibleItemButton href={href ? href : undefined}>
        {startDecorator ? startDecorator : null}
      </CollapsibleItemButton>
    );
  }

  if (href) {
    return (
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      <ListItemButton
        {...props}
        sx={navItemOverride}
        component={NavLink}
        to={href}
      >
        {startDecorator ? startDecorator : null}
        <ListItemContent>
          <NavItemLabel>{label}</NavItemLabel>
        </ListItemContent>
        {endDecorator && !collapsed ? endDecorator : null}
      </ListItemButton>
    );
  }

  return (
    <ListItemButton {...props} sx={navItemOverride}>
      {startDecorator ? startDecorator : null}
      <ListItemContent>
        <NavItemLabel>{label}</NavItemLabel>
      </ListItemContent>
      {endDecorator && !collapsed ? endDecorator : null}
    </ListItemButton>
  );
}

function CollapsibleItemButton({
  href,
  end,
  ...props
}: ListItemButtonProps & {
  href?: NavLinkProps['to'];
} & Pick<NavLinkProps, 'end'>) {
  if (href) {
    return (
      <NavLink
        to={href}
        className={({ isActive }) =>
          clsx([
            'hover:bg-brand-primary-900 hover:text-brand-primary-400 [&>svg]:size-5 text-zinc-400 p-3 rounded-md',
            isActive && '[&>svg]:text-brand-primary-500',
          ])
        }
      >
        {props.children}
      </NavLink>
    );
  }

  return (
    <ListItemButton
      {...props}
      slots={{ root: IconButton }}
      slotProps={{
        root: {
          size: 'lg',
        },
      }}
      sx={navItemOverride}
    />
  );
}

export function NavItemLabel(props: Readonly<TypographyProps>) {
  return <Typography {...props} level='title-md' color='neutral' />;
}
